import { useEffect, useMemo, useRef, useState } from "react";

import { AgGridReact } from "ag-grid-react";
import Box from "@mui/material/Box";

import { withTranslation } from "react-i18next";
import { isEmpty } from "lodash";

import fpBorService from "@plannel/services/fp/fp-bor-service";
import fpResourceService from "@plannel/services/fp/fp-resource-service";
import Dialog from "@plannel/components/Dialog";
import Snackbar from "@plannel/components/Snackbar";
import DataState from "@plannel/components/aggrid/DataState";
import FpGridSetting from "@plannel/pages/factory-plan/common/grid";
import { RemoveButton, SaveButton } from "@plannel/components/ActionIconButton";
import Button from "@mui/material/Button";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import GridUtils from "@plannel/components/aggrid/GridUtils";
import debug from "@plannel/utils/debug-util";
import ColorUtil from "@plannel/pages/utils/color-util";

const { snackBarMessageColor } = ColorUtil;

// TODO(MIG-T2): [bor/ToolSettingTab] 자원 그리드 "이미 사용중인 툴은 선택 불가" 동적 체크
//   제한 미이관.
//   old: setToolResourceGridCheckBox() — 선택된 자원 세트(borSetCd)에 이미 등록된 툴 자원은
//     toolResourceGrid에서 체크 불가(checkable=false) 처리.
//   사유: 장식성 UX 가드(중복 등록해도 저장 자체는 막히지 않음, old도 서버 검증에 의존) —
//     1차 CRUD 목표(조회/등록/저장)엔 불필요.

/**
 * 툴 자원 설정 탭(old 내부 파일명은 ToolSettingTab.jsx이지만 컴포넌트/URL은 "borsettools" —
 * BOR 세트에 사용할 툴 자원을 등록) — old ToolSettingTab.jsx 대응. 좌측 그리드(toolUseGrid)에서
 * 행을 클릭해 컨텍스트(공정/자원 세트)를 고른 뒤, 우측 그리드(toolResourceGrid)에서 툴 자원을
 * 체크하고 추가 버튼으로 좌측에 신규 행을 삽입한다.
 *
 * props: t, viewName, planScope, searchFields
 */
const ToolSettingTab = ({ t, viewName, planScope, searchFields }) => {
  const toolUseGridRef = useRef();
  const toolResourceGridRef = useRef();

  const [toolUse, setToolUse] = useState([]);
  const [toolResource, setToolResource] = useState([]);
  const [currentToolUseRow, setCurrentToolUseRow] = useState(null);

  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });
  const [dialogInfo, setDialogInfo] = useState({ open: false, title: "", content: "" });

  const latestReqRef = useRef(0);

  const toolUseGridMemo = useMemo(() => ({
    ...FpGridSetting({ title: t("FP_TOOL_SETTING"), viewName: `${viewName}_USE` }),
    // old routeCd/routeNm/borSetCd 3개 컬럼 mergeRule(criteria:"value", ToolSettingTab.jsx) 대응 —
    // 셀 병합(spanRows)으로 재현한다. Master(CRUD) 화면은 행 단위 체크/편집/저장이 핵심이라 트리
    // (행 그룹)로 접으면 그 모델이 깨지므로, 행 구조를 그대로 유지하는 spanRows를 쓴다(FP Report
    // 화면들의 셀 병합 선례와 동일 판단, 2026-08-12 사용자 지시). CellSpanModule은 common/grid.js가
    // 이미 전역 등록해둬서 enableCellSpan만 켜면 된다.
    enableCellSpan: true,
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [t, viewName]);
  const toolResourceGridMemo = useMemo(() => ({
    ...FpGridSetting({ title: t("FP_RESOURCE"), viewName: `${viewName}_RES` }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [t, viewName]);

  // rowData는 절대 새 배열로 갈아치우지 않는다(§6-1 — getRowId 없는 상태에서 rowData 참조가 바뀌면
  // grid row model이 재생성되어 addToolResources로 추가한 미저장 신규 행이 조회조건 변경 시
  // 사라지는 버그가 있었다).
  const doesExternalFilterPass = (node) => {
    if (!node.data?.id) return true;   // 미저장 신규 행은 조회조건과 무관하게 항상 표시(편집 중 초안)
    const { routeCdNm, resourceCdNm } = searchFields;
    const hit = (hay, needle) => (hay ?? "").toLowerCase().includes(needle.toLowerCase());
    const r = node.data;
    if (routeCdNm && !(hit(r.routeCd, routeCdNm) || hit(r.routeNm, routeCdNm))) return false;
    if (resourceCdNm && !(hit(r.toolResourceCd, resourceCdNm) || hit(r.toolResourceNm, resourceCdNm))) return false;
    return true;
  };

  const isExternalFilterPresent = () => !!(searchFields.routeCdNm || searchFields.resourceCdNm);

  // searchFields는 AG Grid가 직접 구독하는 값이 아니라서, 바뀔 때마다 명시적으로 재평가를 트리거.
  useEffect(() => {
    toolUseGridRef.current?.api?.onFilterChanged();
  }, [searchFields]);

  // 병합(spanRows) 컬럼 공통 스타일 — 수직 중앙 정렬에 display:flex를 쓰면 안 된다. AG Grid는
  // 셀 안에 텍스트를 감싸는 요소 없이 직접 넣는데, flex 컨테이너 안의 raw 텍스트는 "익명 flex
  // 아이템"이 되고 CSS 명세상 익명 flex 아이템에는 overflow/text-overflow를 적용할 수 없어
  // 말줄임(...)이 원천적으로 불가능하다(minWidth:0/width:100% 같은 우회도 안 통함 — 2026-08-12 확인).
  // 대신 block 레이아웃을 유지한 채 align-content로 수직 중앙 정렬한다(block 컨테이너의
  // align-content는 최신 브라우저 지원) — 이러면 말줄임이 일반 셀과 동일하게 동작한다.
  const MERGED_CELL_STYLE = {
    display: "block", alignContent: "center",
    overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis"
  };

  // old routeGroup/resourceGroup 둘 다 expandable:false(접기/펼치기 없이 항상 표시) — BorTab의
  // 자원 그룹과 동일 패턴(marryChildren + openByDefault:true, columnGroupShow 없음).
  // resourceGroup의 자식(toolResourceCd/toolResourceNm)은 필드명이 "tool"이 붙어 있어도 old
  // headerText는 범용 "FP_RESOURCE_CD"/"FP_RESOURCE_NM"을 그대로 씀 — Tool 전용 라벨이 아니다.
  const toolUseColumnDefs = [
    { headerName: "ID", field: "id", colId: "id", hide: true, type: ["nonEditableColumn"], cellDataType: false },
    { headerName: "FP_PLAN_SCOPE", field: "planScope", colId: "planScope", hide: true, type: ["nonEditableColumn"] },
    {
      headerName: "FP_ROUTE", groupId: "route", marryChildren: true, openByDefault: true,
      children: [
        {
          headerName: "FP_ROUTE_CD", field: "routeCd", colId: "routeCd", type: ["nonEditableColumn"], width: 150,
          cellStyle: MERGED_CELL_STYLE,
          spanRows: ({ nodeA, nodeB }) => !!nodeA?.data && !!nodeB?.data && nodeA.data.routeCd === nodeB.data.routeCd
        },
        {
          headerName: "FP_ROUTE_NM", field: "routeNm", colId: "routeNm", type: ["nonEditableColumn"], width: 200,
          cellStyle: MERGED_CELL_STYLE,
          spanRows: ({ nodeA, nodeB }) => !!nodeA?.data && !!nodeB?.data && nodeA.data.routeCd === nodeB.data.routeCd && nodeA.data.routeNm === nodeB.data.routeNm
        }
      ]
    },
    {
      headerName: "FP_BOR_SET_CD", field: "borSetCd", colId: "borSetCd", type: ["nonEditableColumn"], width: 150,
      cellStyle: MERGED_CELL_STYLE,
      // borSetCd는 공정(routeCd) 경계를 넘어가면 값이 같아도 병합하지 않는다 — 다른 공정 아래
      // 우연히 같은 borSetCd 문자열이 나와도 잘못 이어붙지 않도록.
      spanRows: ({ nodeA, nodeB }) => !!nodeA?.data && !!nodeB?.data && nodeA.data.routeCd === nodeB.data.routeCd && nodeA.data.borSetCd === nodeB.data.borSetCd
    },
    {
      // mstDescTxt(BorSetToolQueryRepository의 borSetMst.descTxt)는 borSetCd 1개당 마스터 레코드가
      // 하나뿐이라 borSetCd가 같으면 항상 같은 값이다(조인 설계상 보장, old에는 이 컬럼 mergeRule이
      // 없었지만 2026-08-12 사용자 지시로 borSetCd와 동일 기준으로 병합 추가).
      headerName: "FP_DESC_TXT", field: "mstDescTxt", colId: "mstDescTxt", type: ["nonEditableColumn"], width: 130,
      cellStyle: MERGED_CELL_STYLE,
      spanRows: ({ nodeA, nodeB }) => !!nodeA?.data && !!nodeB?.data && nodeA.data.routeCd === nodeB.data.routeCd && nodeA.data.borSetCd === nodeB.data.borSetCd
    },
    {
      headerName: "FP_RESOURCE", groupId: "resource", marryChildren: true, openByDefault: true,
      children: [
        { headerName: "FP_RESOURCE_CD", field: "toolResourceCd", colId: "toolResourceCd", type: ["nonEditableColumn"], width: 150 },
        { headerName: "FP_RESOURCE_NM", field: "toolResourceNm", colId: "toolResourceNm", type: ["nonEditableColumn"], width: 200 }
      ]
    },
    { headerName: "FP_TOOL_USABLE_CNT", field: "usableCnt", colId: "usableCnt", width: 110, type: ["numberColumn", "rightAligned"] }
  ];

  // old toolResourceGridItems — 그룹 없이 flat, resourceCd/resourceNm은 "툴" 자원 전용 목록이라
  // FP_TOOL_RESOURCE_CD/NM 라벨을 쓴다(ToolSupplyTab의 좌측 자원 목록과 동일 사유).
  const toolResourceColumnDefs = [
    { headerName: "FP_TOOL_RESOURCE_CD", field: "resourceCd", colId: "resourceCd", type: ["nonEditableColumn"], width: 150 },
    { headerName: "FP_TOOL_RESOURCE_NM", field: "resourceNm", colId: "resourceNm", type: ["nonEditableColumn"], width: 200 },
    { headerName: "FP_DESC_TXT", field: "descTxt", colId: "descTxt", type: ["nonEditableColumn"], width: 130 },
    { headerName: "FP_TOOL_CNT", field: "toolCnt", colId: "toolCnt", type: ["nonEditableColumn", "numberColumn", "rightAligned"], width: 100 }
  ];

  const retrieveToolUse = () => {
    if (!planScope) return;
    const reqId = ++latestReqRef.current;
    toolUseGridRef.current?.api?.setGridOption("loading", true);

    fpBorService.getBorSetTools({ planScope, routeCdNm: searchFields.routeCdNm, resourceCdNm: searchFields.resourceCdNm })
      .then(response => {
        if (reqId !== latestReqRef.current) return;
        setToolUse(isEmpty(response.data) ? [] : response.data);
      })
      .catch(e => debug(e))
      .then(() => {
        if (reqId !== latestReqRef.current) return;
        DataState.initialize(toolUseGridRef.current?.api);
      })
      .finally(() => {
        if (reqId === latestReqRef.current) {
          toolUseGridRef.current?.api?.setGridOption("loading", false);
        }
      });
  };

  const retrieveToolResource = () => {
    if (!planScope) return;
    toolResourceGridRef.current?.api?.setGridOption("loading", true);
    fpResourceService.getAll({ planScope, isTool: true })
      .then(response => setToolResource(isEmpty(response.data) ? [] : response.data))
      .catch(e => debug(e))
      .finally(() => {
        toolResourceGridRef.current?.api?.setGridOption("loading", false);
      });
  };

  const onGridReady = params => {
    GridUtils.setColumnDefs({ ...params, columnDefs: toolUseColumnDefs, viewName: `${viewName}_USE`, initState: true });
    retrieveToolUse();
    retrieveToolResource();
  };

  // GridUtils.setColumnDefs를 거쳐야 헤더가 i18n 번역된다 — columnDefs를 그리드에 prop으로 직접
  // 넘기면 raw 키 문자열이 그대로 노출된다.
  const onToolResourceGridReady = params => {
    GridUtils.setColumnDefs({ ...params, columnDefs: toolResourceColumnDefs, viewName: `${viewName}_RES`, initState: true });
  };

  const onToolUseCellClicked = (e) => {
    if (!e.data) return;
    setCurrentToolUseRow(e.data);
  };

  const addToolResources = () => {
    if (!currentToolUseRow) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("FP_MSG_RESOURCE_SET_ROW_SELECT") });
      return;
    }

    const checkedResources = toolResourceGridRef.current.api.getSelectedRows();
    if (checkedResources.length === 0) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("FP_MSG_TOOL_RESOURCE_ROW_SELECT") });
      return;
    }

    checkedResources.forEach(res => {
      const newRow = {
        id: null,
        planScope,
        routeCd: currentToolUseRow.routeCd,
        routeNm: currentToolUseRow.routeNm,
        borSetCd: currentToolUseRow.borSetCd,
        mstDescTxt: currentToolUseRow.mstDescTxt,
        toolResourceCd: res.resourceCd,
        toolResourceNm: res.resourceNm,
        isTool: true,
        usableCnt: 1,
        toolCnt: res.toolCnt
      };
      DataState.setDataState(GridUtils.addRow(toolUseGridRef.current.api, newRow));
    });
  };

  const removeToolUse = () => {
    toolUseGridRef.current.api.stopEditing();
    const selectedRows = toolUseGridRef.current.api.getSelectedRows();

    if (selectedRows.length > 0) {
      fpBorService.removeBorSetTools(selectedRows)
        .then(() => setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_DeleteSuccess") }))
        .catch(() => setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_DeleteFailed") }))
        .then(() => retrieveToolUse());
    } else {
      setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSaveData") });
    }
  };

  const saveToolUse = () => {
    toolUseGridRef.current.api.stopEditing();
    const allStateData = DataState.getAllStateData(toolUseGridRef.current.api);

    if (allStateData.length > 0) {
      fpBorService.saveBorSetTools(allStateData)
        .then(() => setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_SaveSuccess") }))
        .catch(e => { setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_SaveFailed") }); debug(e); })
        .then(() => retrieveToolUse());
    } else {
      setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSaveData") });
    }
  };

  const handleDialog = (action) => {
    if (action === "SAVE") saveToolUse();
    else if (action === "DELETE") removeToolUse();
  };

  // 화면 내부 탭(§11-3): planScope 변경 시에만 재조회. routeCdNm/resourceCdNm은 클라이언트 필터
  // (§6-1 케이스3) — old도 이 두 필드만 사용(itemCdNm은 이 탭에 없음).
  useEffect(() => {
    if (!toolUseGridRef.current?.api) return;
    retrieveToolUse();
    retrieveToolResource();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planScope]);

  return (
    <>
      <Box sx={{ display: "flex", gap: 1, height: "100%" }}>
        <Box sx={{ display: "flex", flexDirection: "column", flex: 1.5 }}>
          {/* 우측 헤더줄과 높이가 달라 그 아래 두 그리드가 수직으로 안 맞던 문제 — 양쪽 헤더줄에
              minHeight+alignItems:center로 높이를 맞춘다. */}
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", mb: 1, minHeight: 40 }}>
            <RemoveButton onClick={() => setDialogInfo({ open: true, title: "delete", content: t("MSG_DeleteConfirm") })} />
            <SaveButton onClick={() => setDialogInfo({ open: true, title: "save", content: t("MSG_SaveConfirm") })} />
          </Box>
          <Box id="fp-tool-use-grid" className="ag-theme-balham grid-area" sx={{ flex: 1, pb: 1 }}>
            <AgGridReact
              ref={toolUseGridRef}
              rowData={toolUse}
              {...toolUseGridMemo}
              rowSelection={{ ...toolUseGridMemo.rowSelection, headerCheckbox: true, checkboxes: true }}
              isExternalFilterPresent={isExternalFilterPresent}
              doesExternalFilterPass={doesExternalFilterPass}
              onCellClicked={onToolUseCellClicked}
              onGridReady={onGridReady}
            />
          </Box>
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column", flex: 1 }}>
          {/* old: 추가 버튼(← 아이콘) + SEARCH 버튼(toolResourceGrid 수동 재조회) — old
              CommonButton startIcon={<ArrowLeft/>} 대응, "자원" 라벨은 old에 없던 것이라 제거. */}
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1, minHeight: 40 }}>
            <Button size="small" startIcon={<ArrowBackIcon fontSize="small" />} onClick={addToolResources}>{t("FP_ADD_TOOL_RESOURCE")}</Button>
            <Button size="small" onClick={retrieveToolResource}>{t("search")}</Button>
          </Box>
          <Box id="fp-tool-resource-grid" className="ag-theme-balham grid-area" sx={{ flex: 1, pb: 1 }}>
            <AgGridReact
              ref={toolResourceGridRef}
              rowData={toolResource}
              {...toolResourceGridMemo}
              rowSelection={{ ...toolResourceGridMemo.rowSelection, headerCheckbox: true, checkboxes: true }}
              onGridReady={onToolResourceGridReady}
            />
          </Box>
        </Box>
      </Box>
      <Dialog
        open={dialogInfo.open}
        onClose={() => setDialogInfo({ open: false, title: "", content: "" })}
        title={dialogInfo.title}
        content={dialogInfo.content}
        onHandler={handleDialog}
      />
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
    </>
  );
};

export default withTranslation()(ToolSettingTab);
